import api from "./client";

/* ── Dashboard ── */

export async function getDashboardStats() {
  const { data } = await api.get("/api/admin/dashboard");
  return data.data.stats;
}

export async function getRecentOrders() {
  const { data } = await api.get("/api/admin/dashboard");
  return data.data.recentOrders;
}

export async function getLowStockProducts() {
  const { data } = await api.get("/api/admin/dashboard");
  return data.data.lowStock;
}

// Combined dashboard call to avoid 3 separate requests
export async function getDashboard() {
  const { data } = await api.get("/api/admin/dashboard");
  return data.data;
}

/* ── Orders ── */

export async function getOrders(params = {}) {
  const qs = new URLSearchParams();
  if (params.status && params.status !== 'ALL' && params.status !== 'All') qs.append('status', params.status);
  if (params.search) qs.append('search', params.search);
  if (params.sortBy) qs.append('sortBy', params.sortBy);
  qs.append('page', params.page || 1);
  qs.append('limit', params.limit || 10);
  const { data } = await api.get(`/api/admin/orders?${qs}`);
  return { orders: data.data, total: data.total, page: data.page, limit: data.limit };
}

export async function getOrderCounts() {
  const { data } = await api.get('/api/admin/orders/counts');
  return data.data;
}

export async function updateOrderStatus(orderId, status) {
  const { data } = await api.put(`/api/admin/orders/${orderId}/status`, {
    status: status.toUpperCase(),
  });
  return data.data;
}

export async function updateOrderPaymentStatus(orderId, paymentStatus) {
  const { data } = await api.put(`/api/admin/orders/${orderId}/payment-status`, {
    paymentStatus,
  });
  return data.data;
}

/* ── Products CRUD ── */

export async function getProducts(params = {}) {
  const qs = new URLSearchParams();
  if (params.category && params.category !== 'All') qs.append('category', params.category);
  if (params.search) qs.append('search', params.search);
  qs.append('page', params.page || 1);
  qs.append('limit', params.limit || 10);
  const { data } = await api.get(`/api/admin/products?${qs}`);
  return {
    products: data.data,
    categories: ["All", "T-Shirts", "Pants", "Corset"],
    total: data.total,
    page: data.page,
    limit: data.limit,
  };
}

export async function createProduct(product) {
  const { data } = await api.post("/api/admin/products", product);
  return data.data;
}

export async function updateProduct(id, updates) {
  const { data } = await api.put(`/api/admin/products/${id}`, updates);
  return data.data;
}

export async function deleteProduct(id) {
  const { data } = await api.delete(`/api/admin/products/${id}`);
  return data.data;
}

/* ── Product Import/Export ── */

export async function exportProducts() {
  const { data } = await api.get("/api/admin/products/export", {
    responseType: "blob",
  });
  return data;
}

export async function exportProductsCSV() {
  const { data } = await api.get("/api/admin/products/export-csv", {
    responseType: "blob",
  });
  return data;
}

export async function importProducts(file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post("/api/admin/products/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.data;
}

/* ── Upload ── */

export async function uploadImages(files) {
  const formData = new FormData();
  files.forEach((file) => formData.append("images", file));
  const { data } = await api.post("/api/admin/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.data; // array of URLs
}

/* ── Promo Codes ── */

export async function getPromoCodes(params = {}) {
  const qs = new URLSearchParams();
  if (params.search) qs.append('search', params.search);
  qs.append('page', params.page || 1);
  qs.append('limit', params.limit || 10);
  const { data } = await api.get(`/api/admin/promo-codes?${qs}`);
  return { promos: data.data, total: data.total, page: data.page, limit: data.limit };
}

export async function createPromoCode(promo) {
  const { data } = await api.post("/api/admin/promo-codes", promo);
  return data.data;
}

export async function updatePromoCode(id, updates) {
  const { data } = await api.put(`/api/admin/promo-codes/${id}`, updates);
  return data.data;
}

export async function deletePromoCode(id) {
  const { data } = await api.delete(`/api/admin/promo-codes/${id}`);
  return data.data;
}

/* ── Subscribers ── */

export async function getSubscribers(params = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.append("page", params.page);
  if (params.limit) qs.append("limit", params.limit);
  if (params.search) qs.append("search", params.search);
  const { data } = await api.get(`/api/admin/subscribers?${qs}`);
  return data.data;
}

export async function toggleSubscriberActive(id) {
  const { data } = await api.patch(`/api/admin/subscribers/${id}/toggle-active`);
  return data.data;
}

export async function exportSubscribersCSV() {
  const { data } = await api.get("/api/admin/subscribers?limit=10000");
  const rows = [["Email", "Promo Code", "Active", "Date"]];
  data.data.subscribers.forEach((s) => {
    rows.push([
      s.email,
      s.promoCode || "",
      s.isActive ? "Yes" : "No",
      new Date(s.createdAt).toLocaleDateString(),
    ]);
  });
  return rows.map((r) => r.join(",")).join("\n");
}

/* ── Analytics ── */

// period is either { days: 7 | 30 | 90 } or { month: "YYYY-MM" }
export async function getAnalytics(period = { days: 30 }) {
  const qs = new URLSearchParams();
  if (period.month) qs.append("month", period.month);
  else qs.append("days", period.days || 30);

  const [overviewRes, revenueRes, statusRes, topRes] = await Promise.all([
    api.get(`/api/admin/analytics/overview?${qs}`),
    api.get(`/api/admin/analytics/revenue?${qs}`),
    api.get(`/api/admin/analytics/orders-by-status?${qs}`),
    api.get(`/api/admin/analytics/top-products?${qs}`),
  ]);

  return {
    overview: overviewRes.data.data,
    revenueDaily: revenueRes.data.data,
    ordersByStatus: statusRes.data.data,
    topProducts: topRes.data.data,
  };
}

export async function getAnalyticsMonths() {
  const { data } = await api.get("/api/admin/analytics/months");
  return data.data;
}

export async function getMonthlyAnalytics(months = 12) {
  const { data } = await api.get(`/api/admin/analytics/monthly?months=${months}`);
  return data.data;
}

// Where visitors and buyers came from, for the same period shape as getAnalytics
export async function getTrafficSources(period = { days: 30 }) {
  const qs = new URLSearchParams();
  if (period.month) qs.append("month", period.month);
  else qs.append("days", period.days || 30);

  const { data } = await api.get(`/api/admin/analytics/sources?${qs}`);
  return { rows: data.data, totals: data.totals };
}
